import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import Typography from '@mui/material/Typography';
import {
  AccountData,
  AccountService,
  type FreeEscortsPluginData,
  OrderPageData,
  type PartialLabelData,
  type WeekendAndIsraeliHolidaysPluginData,
} from '../../../api';
import { OrderPagePrice, OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { FormTextField } from '../../formComponents/FormTextField';
import { FormLabelField } from '../../formComponents/FormLabelField';
import { useAuth } from '../../../session/InternalAuthProvider';
import { FormCheckbox } from '../../formComponents/FormCheckBox';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { DEFAULT_PRICE_UNIT_LABEL, isBasePricing, isCustomUnitPricing, isPersonsNumberPrice } from './utils';
import { MULTIPLE_TICKETS_PRICE_TYPE } from '../../../common/multipleTicketsUtils';
import {
  buildMultipleTicketsInput,
  initMultipleTicketsForm,
  MultipleTicketsFormValues,
  MultipleTicketsPricingFields,
} from './MultipleTicketsPricingFields';
import { BasePricingFields } from './BasePricingFields';
import { CustomUnitPricingFields } from './CustomUnitPricingFields';
import { getCurrencySymbol } from '../../../utils/utils';
import { PluginType } from '../../../common/PluginUtils';
import { useLanguage } from '../../../common/GeneralUtils';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../saveHeader/SaveHeaderContext';
import { collect, diffBoolean, diffNumber } from '../../saveHeader/diffBuilders';
import { SettingsPageTabs } from '../../SettingsPageTabs';

type PricingSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
};

export type PricingFormValues = {
  price: OrderPagePrice[];
  weekend_percentage_enabled?: boolean;
  weekend_percentage?: number;
  multiple_tickets?: MultipleTicketsFormValues;
  price_unit_label?: PartialLabelData;
  show_price_as_deposit?: boolean;
};

function parseToPerc(value: number): number {
  return Math.round(value * 100 - 100);
}

function parseToDecimal(value: number): number {
  return 1 + value / 100;
}

export const PricingSection = ({ orderPage, orderPageInitialValues, updateLocalAccount }: PricingSectionProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const language = useLanguage();

  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const currencySymbol = getCurrencySymbol(authState.account as unknown as AccountData);
  const activityLabel: string =
    orderPage.activity_label?.[language] || t('editOrderPage.activityFallback') || 'workshop';
  const validationSchema = yup.object().shape({
    weekend_percentage_enabled: yup.boolean(),
    weekend_percentage: yup
      .number()

      .when('weekend_percentage_enabled', {
        is: true,
        then: yup
          .number()
          .required('Weekend percentage is required')
          .moreThan(0, 'Percentage must be more than 0')
          .lessThan(100, 'Percentage must be less than 100'),
      }),
  });
  const weekend_percentage_enabled = orderPage.plugins?.find(
    (plugin: WeekendAndIsraeliHolidaysPluginData | FreeEscortsPluginData) =>
      plugin.type === PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS,
  );
  const account = authState.account as unknown as AccountData;
  const languages = account.customer_facing_widgets_languages;
  const initialValues: PricingFormValues = {
    price: orderPage.price as OrderPagePrice[],
    weekend_percentage_enabled: !!weekend_percentage_enabled,
    // @ts-ignore
    weekend_percentage: weekend_percentage_enabled ? parseToPerc(weekend_percentage_enabled.multiply) : 0,
    multiple_tickets: initMultipleTicketsForm(orderPage),
    // Prefilled rather than left blank: the field reads as "rename the unit", not "opt into a mode".
    price_unit_label: orderPage.price_unit_label || DEFAULT_PRICE_UNIT_LABEL,
    show_price_as_deposit: !!orderPage.show_price_as_deposit,
  };

  const handleSubmit = async (values: PricingFormValues, { setStatus }: FormikHelpers<PricingFormValues>) => {
    try {
      posthog.capture('Editing settings', { section: 'pricingSection', settings: values });
      const { multiple_tickets: mt, ...rest } = values;
      const includeMt = mt && mt.tickets.length > 0;
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            duration: (orderPageInitialValues.duration || 0) * 60,
            ...rest,
            weekend_percentage: parseToDecimal(values.weekend_percentage || 0),
            id: orderPage.id,
            multiple_tickets: includeMt ? buildMultipleTicketsInput(mt, languages) : undefined,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const priceRowLabel = (p: OrderPagePrice | undefined, index: number, count: number): string => {
    // When there are multiple price rows, suffix with #N; single-row cases omit.
    const suffix = count > 1 ? ` #${index + 1}` : '';
    if (p?.name) return `${p.name}${suffix}`;
    if (p?.type === MULTIPLE_TICKETS_PRICE_TYPE) {
      return `${t('editOrderPage.multipleTickets', { defaultValue: 'Multiple tickets' })}${suffix}`;
    }
    return `${t('editOrderPage.basePrice')}${suffix}`;
  };

  const diffPriceRow = (
    before: OrderPagePrice | undefined,
    after: OrderPagePrice | undefined,
    prefix: string,
  ): ChangeEntry[] => {
    const rows: ChangeEntry[] = [];
    // base_price
    const basePrice = diffNumber(`${prefix} · ${t('editOrderPage.basePrice')}`, before?.base_price, after?.base_price, {
      prefix: currencySymbol,
    });
    if (basePrice) rows.push(basePrice);
    // base_value (participants count)
    const baseValue = diffNumber(
      `${prefix} · ${t('editOrderPage.for')} ${t('editOrderPage.persons')}`,
      before?.base_value,
      after?.base_value,
    );
    if (baseValue) rows.push(baseValue);
    // price_per_additional_value
    const perAdditional = diffNumber(
      `${prefix} · ${t('editOrderPage.forAdditional')}`,
      before?.price_per_additional_value,
      after?.price_per_additional_value,
      { prefix: currencySymbol },
    );
    if (perAdditional) rows.push(perAdditional);
    // tiered_pricing / custom_per_unit_pricing — coarse summary only
    if (JSON.stringify(before?.tiered_pricing) !== JSON.stringify(after?.tiered_pricing)) {
      rows.push({
        label: `${prefix} · ${t('editOrderPage.tieredPricing', { defaultValue: 'Tiered pricing' })}`,
        from: `${before?.tiered_pricing?.length || 0}`,
        to: `${after?.tiered_pricing?.length || 0}`,
      });
    }
    if (JSON.stringify(before?.custom_per_unit_pricing) !== JSON.stringify(after?.custom_per_unit_pricing)) {
      rows.push({
        label: `${prefix} · ${t('editOrderPage.customUnitPricing', { defaultValue: 'Per-unit pricing' })}`,
        from: (before?.custom_per_unit_pricing || []).map((n) => `${currencySymbol}${n}`).join(', ') || '—',
        to: (after?.custom_per_unit_pricing || []).map((n) => `${currencySymbol}${n}`).join(', ') || '—',
      });
    }
    return rows;
  };

  const getChanges = (values: PricingFormValues, iv: PricingFormValues): ChangeEntry[] => {
    const priceRows: ChangeEntry[] = [];
    const maxLen = Math.max(values.price?.length || 0, iv.price?.length || 0);
    for (let i = 0; i < maxLen; i += 1) {
      const before = iv.price?.[i];
      const after = values.price?.[i];
      const prefix = priceRowLabel(after || before, i, maxLen);
      priceRows.push(...diffPriceRow(before, after, prefix));
    }
    const multiTicketsChanged = JSON.stringify(values.multiple_tickets) !== JSON.stringify(iv.multiple_tickets);
    const unitLabelRows: ChangeEntry[] = [];
    languages.forEach((lang) => {
      if ((values.price_unit_label?.[lang] || '') !== (iv.price_unit_label?.[lang] || '')) {
        unitLabelRows.push({
          label: `${t('editOrderPage.priceUnitLabel')} (${t(`emailTemplates.${lang}`)})`,
          from: iv.price_unit_label?.[lang] || '—',
          to: values.price_unit_label?.[lang] || '—',
        });
      }
    });
    return [
      ...priceRows,
      ...unitLabelRows,
      ...collect(
        diffBoolean(t('editOrderPage.showPriceAsDeposit'), iv.show_price_as_deposit, values.show_price_as_deposit),
        diffBoolean(
          t('editOrderPage.weekendNHolidays'),
          iv.weekend_percentage_enabled,
          values.weekend_percentage_enabled,
        ),
        diffNumber(`${t('editOrderPage.weekendNHolidays')} %`, iv.weekend_percentage, values.weekend_percentage, {
          suffix: '%',
        }),
        multiTicketsChanged
          ? {
              label: t('editOrderPage.multipleTickets', { defaultValue: 'Multiple tickets' }),
              from: `${iv.multiple_tickets?.tickets?.length || 0}`,
              to: `${values.multiple_tickets?.tickets?.length || 0}`,
            }
          : null,
      ),
    ];
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        // Read from the form, not the saved page, so renaming the unit retitles the rows as you type.
        const unitLabel = values.price_unit_label?.[language] || '';
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={SettingsPageTabs.PRICING} getChanges={getChanges} disabled={readOnly} />
            <Grid container flexDirection="column">
              {values.price.map((price, i) => {
                if (price.type === MULTIPLE_TICKETS_PRICE_TYPE) {
                  return (
                    <SettingsRow key={i} label={t('editOrderPage.multipleTickets.title')}>
                      <MultipleTicketsPricingFields
                        formIsDisabled={formIsDisabled}
                        currencySymbol={currencySymbol}
                        activityLabel={activityLabel}
                      />
                    </SettingsRow>
                  );
                }
                if (isPersonsNumberPrice(price) && isBasePricing(price)) {
                  return (
                    <SettingsRow key={i} label={t('tabs.pricing')}>
                      <BasePricingFields
                        price={price}
                        index={i}
                        formIsDisabled={formIsDisabled}
                        currencySymbol={currencySymbol}
                        unitLabel={unitLabel}
                      />
                    </SettingsRow>
                  );
                }
                if (isCustomUnitPricing(price)) {
                  return (
                    <SettingsRow
                      key={i}
                      label={
                        unitLabel
                          ? t('editOrderPage.customPricingUnit', { unit: unitLabel })
                          : t('editOrderPage.customPricing')
                      }>
                      <CustomUnitPricingFields
                        price={price}
                        index={i}
                        formIsDisabled={formIsDisabled}
                        currencySymbol={currencySymbol}
                        unitLabel={unitLabel}
                      />
                    </SettingsRow>
                  );
                }
                return (
                  <SettingsRow key={i} label={t('tabs.pricing')}>
                    <Typography>{t('editOrderPage.notSupported')}</Typography>
                  </SettingsRow>
                );
              })}
              <SettingsRow label={t('editOrderPage.additions')}>
                <Grid container flexWrap="wrap" gap={2} alignItems="center">
                  <Grid item>
                    <FormCheckbox
                      label={t('editOrderPage.weekendNHolidays')}
                      fieldName={'weekend_percentage_enabled'}
                      disabled={formIsDisabled}
                      isFieldUpdate
                    />
                  </Grid>

                  <Grid item>
                    <FormTextField
                      width={90}
                      fieldName={'weekend_percentage'}
                      disabled={formIsDisabled || !values.weekend_percentage_enabled}
                      isFieldUpdate
                      endAdornment={'%'}
                    />
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('editOrderPage.notobvious')}
                </Typography>
              </SettingsRow>
              <SettingsRow label={t('editOrderPage.priceWording')} isLast>
                <Grid container flexDirection="column" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('editOrderPage.priceUnitLabelCaption')}
                  </Typography>
                  <FormLabelField
                    labelFieldName="price_unit_label"
                    label={t('editOrderPage.priceUnitLabel')}
                    disabled={formIsDisabled}
                  />
                  <Grid container flexDirection="column" gap={1}>
                    <FormCheckbox
                      label={t('editOrderPage.showPriceAsDeposit')}
                      fieldName={'show_price_as_deposit'}
                      disabled={formIsDisabled}
                      isFieldUpdate
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('editOrderPage.showPriceAsDepositCaption')}
                    </Typography>
                  </Grid>
                </Grid>
              </SettingsRow>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
